import { Icon, IconButton, Menu, MenuButton, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiMessageSquare, FiMoreHorizontal, FiSave, FiTrash2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  SendForCommentRefusalReason,
  SourcingEventStatus,
  useDeleteSourcingEventMutation,
  useSendProcurementForCommentMutation,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../common/errors/useApiError";
import { ALL_SECTIONS, buildTemplateContent } from "../../../../../common/library/templateFromEvent";
import { sectionsDefinedBy } from "../../../../../common/library/templateSelection";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { urls } from "../../../../../urls";
import { useCommentRounds } from "./commentRound/CommentRoundContext";

interface Props {
  eventId: string;
}

export const WizardActionsMenu = ({ eventId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trigger } = useApiError();
  const confirmationModal = useConfirmationModal();
  const [deleteEvent] = useDeleteSourcingEventMutation();
  const [sendForComment, { isLoading: isSendingForComment }] = useSendProcurementForCommentMutation();
  const commentRounds = useCommentRounds();
  const { state, flushPending } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const { selectedOrg } = useLoggedInWithOrgContextState();

  const confirmDiscard = () => {
    confirmationModal.show({
      title: t("Discard the draft?"),
      description: t("The draft and everything registered on it is permanently deleted."),
      confirmButtonColor: "red",
      confirmButtonText: t("Discard") ?? "",
      onConfirmed: async () => {
        try {
          await deleteEvent({ eventId }).unwrap();
          navigate(urls.app.sourcingEvents.viewAll);
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const confirmSendForComment = () => {
    confirmationModal.show({
      title: t("Send the draft out for comment?"),
      description: t(
        "A round of a market dialogue is prepared with a copy of the minimum requirements, qualification requirements, award criteria, price form and documents as they are now. Before it is sent you choose the answer deadline, and whether it is announced on Doffin or sent to invited suppliers. The procurement stays editable.",
      ),
      confirmButtonText: t("Prepare the round") ?? "",
      onConfirmed: async () => {
        try {
          await flushPending();
          const round = await sendForComment({ sourcingEventId: eventId }).unwrap();
          navigate(urls.app.marketDialogues.view.go(round.id));
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const saveAsTemplate = async () => {
    try {
      await flushPending();
    } catch (e) {
      trigger(e);
      return;
    }
    navigate(urls.app.maler.new.go({ fromEvent: eventId }));
  };

  const canDiscard = state.status === SourcingEventStatus.Draft;
  const canSaveAsTemplate = selectedOrg.canEditDocumentTemplates;
  const hasNothingToTemplate =
    sectionsDefinedBy(buildTemplateContent(state.eventData, ALL_SECTIONS)).length === 0;

  const canSendForComment = commentRounds?.canSendForComment === true;
  const refusal = commentRounds?.sendForCommentRefusal;
  const roundOpen = refusal?.reason === SendForCommentRefusalReason.RoundOpen ? refusal : undefined;
  const roundInPreparation =
    refusal?.reason === SendForCommentRefusalReason.RoundInPreparation ? refusal : undefined;
  const offersSendForComment = canSendForComment || roundOpen !== undefined || roundInPreparation !== undefined;
  const openRoundInPreparation = () => {
    if (roundInPreparation?.dialogueId) navigate(urls.app.marketDialogues.view.go(roundInPreparation.dialogueId));
  };

  if (!canDiscard && !canSaveAsTemplate && !offersSendForComment) return null;

  return (
    <>
      {confirmationModal.modal}
      <Menu>
        <MenuButton
          as={IconButton}
          size="sm"
          variant="outline"
          aria-label={t("Actions") ?? "Actions"}
          icon={<Icon as={FiMoreHorizontal} />}
        />
        <MenuList>
          {offersSendForComment && (
            <MenuItem
              icon={<Icon as={FiMessageSquare} />}
              isDisabled={isSendingForComment || roundOpen !== undefined}
              onClick={roundInPreparation ? openRoundInPreparation : confirmSendForComment}
            >
              <Text>{t("Send out for comment")}</Text>
              {roundOpen && (
                <Text fontSize="xs" color="smMuted">
                  {t("Round {{number}} of the market dialogue is still open. A new round can be sent once its answer deadline has passed.", {
                    number: roundOpen.roundNumber,
                  })}
                </Text>
              )}
              {roundInPreparation && (
                <>
                  <Text fontSize="xs" color="smMuted">
                    {t("Round {{number}} is being prepared. Publish or delete it before you send a new round.", {
                      number: roundInPreparation.roundNumber,
                    })}
                  </Text>
                  <Text fontSize="xs" color="smPrimary" fontWeight="semibold">
                    {t("Open round {{number}}", { number: roundInPreparation.roundNumber })}
                  </Text>
                </>
              )}
            </MenuItem>
          )}
          {canSaveAsTemplate && (
            <MenuItem
              icon={<Icon as={FiSave} />}
              isDisabled={hasNothingToTemplate}
              onClick={() => void saveAsTemplate()}
            >
              {t("Save as template")}
            </MenuItem>
          )}
          {canDiscard && (
            <MenuItem color="smError" icon={<Icon as={FiTrash2} />} onClick={confirmDiscard}>
              {t("Discard the draft")}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </>
  );
};
